import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { FaShieldAlt } from 'react-icons/fa';
import { apiFetch } from '../../utils/apiClient';
import { getFreshAccessToken, clientSessionUsable } from '../../utils/authClient';
import { useProjectData } from './context/ProjectDataContext';
import ConnectClaudeModal from './components/ConnectClaudeModal';
import SecurityModal from './components/SecurityModal';

/**
 * ⚠️ DEBOUNCED SEPARATELY FROM THE CONTEXT'S OWN 500ms (LIV-15).
 *
 * `project_updated` fires on every task and comment edit in the project, and
 * `/client/team-members` confirms each candidate against Cognito - so an open
 * Team page would otherwise cost N Cognito calls per task edit.
 */
const TEAM_REFRESH_DEBOUNCE_MS = 500;

export default function ClientAdmin() {
    const navigate = useNavigate();
    const [activeTab, setActiveTab] = useState('account');
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [showInviteModal, setShowInviteModal] = useState(false);
    const [showPasswordModal, setShowPasswordModal] = useState(false);
    const [showConnectClaude, setShowConnectClaude] = useState(false);
    // Own MFA state, for the Connect Claude gate. null = unknown, and unknown
    // must never render as "off" - §9.9 requires MFA for a connector, so a
    // wrong "off" would send someone to set up something they already have.
    const [mfaEnabled, setMfaEnabled] = useState(null);
    const [mfaStatusFailed, setMfaStatusFailed] = useState(false);
    const [showSecurityModal, setShowSecurityModal] = useState(false);

    // The caller's own MFA state, for the Connect Claude gate. The status
    // endpoint authenticates with the ACCESS token, not the ID token, so it
    // cannot go through apiFetch. A failed check stays "unknown" and never
    // renders as "off": telling someone to set up MFA they already have is
    // worse than letting the server refuse them.
    useEffect(() => {
        let cancelled = false;
        (async () => {
            try {
                const accessToken = await getFreshAccessToken();
                if (!accessToken) {
                    if (!cancelled) setMfaStatusFailed(true);
                    return;
                }
                const res = await fetch(`${process.env.REACT_APP_API_URL}/client/mfa/status`, {
                    headers: { Authorization: `Bearer ${accessToken}` }
                });
                if (!res.ok) throw new Error(`MFA status returned ${res.status}`);
                const data = await res.json();
                if (!cancelled) {
                    setMfaEnabled(data.mfaEnabled === true);
                    setMfaStatusFailed(false);
                }
            } catch (err) {
                console.error('MFA status check failed:', err);
                if (!cancelled) setMfaStatusFailed(true);
            }
        })();
        return () => { cancelled = true; };
    }, []);
    const [passwordForm, setPasswordForm] = useState({
        currentPassword: '',
        newPassword: '',
        confirmPassword: ''
    });
    const [changingPassword, setChangingPassword] = useState(false);

    // User data
    const [userInfo, setUserInfo] = useState({
        name: '',
        email: '',
        company: '',
        role: 'user'
    });

    // Team members data
    const [teamMembers, setTeamMembers] = useState([]);

    // Invite form
    const [inviteForm, setInviteForm] = useState({
        name: '',
        email: ''
    });
    const [inviting, setInviting] = useState(false);

    // Auth data from localStorage
    const token = localStorage.getItem('clientToken');
    const leadId = localStorage.getItem('clientLeadId');
    const timestamp = localStorage.getItem('clientTimestamp');

    useEffect(() => {
        if (!token || !leadId || !timestamp) {
            navigate('/client/login');
            return;
        }

        fetchData();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [token, leadId, timestamp, navigate]);

    // -----------------------------------------------------------------------
    // Live updates (LIV-15). ⛔ NO SOCKET OF ITS OWN.
    //
    // This page renders under ClientPortalLayout, which mounts
    // ProjectDataProvider - and that provider already owns the client's ONE
    // connection. A second one would mean a second connections row, a second
    // $connect authorisation, doubled fan-out from every stream record and
    // doubled pings, per tab, for nothing.
    //
    // ⚠️ IT REACTS TO BOTH SIGNALS. `team_updated` has exactly ONE emitter - the
    // role-change path - and invite, remove, disable and enable write the
    // MEMBERS row instead, arriving as `project_updated`. The context bumps
    // `teamLastUpdate` for both; reacting to one of them would fix F3 for role
    // changes only and look done.
    // -----------------------------------------------------------------------
    const { teamLastUpdate } = useProjectData();

    // GATE 3 - the page's own flags. ⚠️ `showPasswordModal`, `showConnectClaude`
    // and `showSecurityModal` are considered and EXCLUDED on purpose: they hold
    // the signed-in user's own password, MFA and connector state and no team
    // data, so refreshing the team list beneath them changes nothing they are
    // editing. Listed here so a later pass does not find them as an omission.
    const liveRefreshBlocked = Boolean(showInviteModal || inviting);
    const liveRefreshBlockedRef = useRef(liveRefreshBlocked);
    useEffect(() => { liveRefreshBlockedRef.current = liveRefreshBlocked; }, [liveRefreshBlocked]);

    const pendingRefetchRef = useRef(false);
    const refreshTimerRef = useRef(null);
    const firstTeamSignalRef = useRef(true);

    useEffect(() => () => {
        if (refreshTimerRef.current) clearTimeout(refreshTimerRef.current);
    }, []);

    useEffect(() => {
        // The context seeds the timestamp at mount, and the mount effect above
        // has already fetched.
        if (firstTeamSignalRef.current) {
            firstTeamSignalRef.current = false;
            return;
        }
        // GATE 3 - hold it (D11); the flush effect runs it once when the modal
        // closes, however many arrived.
        if (liveRefreshBlockedRef.current) {
            pendingRefetchRef.current = true;
            return;
        }
        if (refreshTimerRef.current) clearTimeout(refreshTimerRef.current);
        refreshTimerRef.current = setTimeout(async () => {
            refreshTimerRef.current = null;
            // GATE 0 - renew the session if that is all it needs; skip when it
            // cannot be renewed, so a message can never take apiClient's 401
            // path and redirect a tab nobody has touched.
            if (!await clientSessionUsable()) return;
            fetchData({ silent: true });
        }, TEAM_REFRESH_DEBOUNCE_MS);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [teamLastUpdate]);

    useEffect(() => {
        if (liveRefreshBlocked || !pendingRefetchRef.current) return;
        pendingRefetchRef.current = false;
        if (refreshTimerRef.current) clearTimeout(refreshTimerRef.current);
        refreshTimerRef.current = setTimeout(async () => {
            refreshTimerRef.current = null;
            if (!await clientSessionUsable()) return;
            fetchData({ silent: true });
        }, TEAM_REFRESH_DEBOUNCE_MS);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [liveRefreshBlocked]);

    // ⚠️ SILENT MODE (LIV, D1). The message path passes `{ silent: true }`: no
    // loading state - which on this page replaces the whole team table - and no
    // error banner for a request the user never made.
    const fetchData = async ({ silent = false } = {}) => {
        if (!silent) setLoading(true);
        try {
            // Fetch user info
            const userResponse = await apiFetch(
                `${process.env.REACT_APP_API_URL}/client/lead?leadId=${leadId}&timestamp=${timestamp}`
            );

            if (userResponse.ok) {
                const userData = await userResponse.json();
                const userRole = userData.clientRole || 'user';

                setUserInfo({
                    name: userData.name || '',
                    email: userData.email || '',
                    company: userData.company || '',
                    role: userRole
                });

                // Only fetch team members if admin
                if (userRole === 'admin') {
                    const teamResponse = await apiFetch(
                        `${process.env.REACT_APP_API_URL}/client/team-members?leadId=${leadId}&timestamp=${timestamp}`
                    );

                    if (teamResponse.ok) {
                        const teamData = await teamResponse.json();
                        setTeamMembers(teamData);
                    }
                }
            }

        } catch (err) {
            console.error('Fetch error:', err);
            if (!silent) setError('Failed to load data');
        } finally {
            if (!silent) setLoading(false);
        }
    };

    const handleInviteUser = async () => {
        if (userInfo.role !== 'admin') {
            setError('Only administrators can invite users');
            return;
        }

        if (!inviteForm.name.trim() || !inviteForm.email.trim()) {
            setError('Name and email are required');
            return;
        }

        setInviting(true);
        setError(null);

        try {
            const response = await apiFetch(
                `${process.env.REACT_APP_API_URL}/client/invite-user`,
                {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json'
                    },
                    body: JSON.stringify({
                        name: inviteForm.name,
                        email: inviteForm.email,
                        leadId: leadId,
                        timestamp: timestamp
                    })
                }
            );

            const data = await response.json();

            if (response.ok) {
                setInviteForm({ name: '', email: '' });
                setShowInviteModal(false);
                alert('Invitation sent successfully! They will receive an email with login details.');
                fetchData();
            } else {
                setError(data.error || 'Failed to send invitation');
            }
        } catch (err) {
            console.error('Invite error:', err);
            setError('Failed to send invitation');
        } finally {
            setInviting(false);
        }
    };

    /**
     * Resend a colleague's invitation (F9).
     *
     * The button existed as `alert('Resend invite coming soon!')`. It matters
     * more than a placeholder suggests: an invite can succeed while its email
     * does not, and until this route existed the only person who could resend
     * was The Migration Experts - so a client admin was left with a colleague
     * who had heard nothing and a retry that refused as "already exists".
     *
     * Every refusal comes back as the same 403 with the same words, on purpose:
     * distinguishing them would tell a caller which accounts exist. So the
     * message here is deliberately general rather than guessing at a reason.
     */
    const handleResendInvite = async (member) => {
        if (!window.confirm(`Resend the invitation to ${member.email}?\n\nThey will get a new email with a fresh temporary password. Any previous one stops working.`)) return;

        try {
            const response = await apiFetch(
                `${process.env.REACT_APP_API_URL}/client/resend-invite`,
                {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ targetEmail: member.email })
                }
            );
            const data = await response.json();
            if (response.ok) {
                alert(`Invitation re-sent to ${member.email}.`);
                fetchData();
            } else {
                alert(data.error || 'Could not resend the invitation');
            }
        } catch (err) {
            console.error('Resend invite failed:', err);
            alert('Could not resend the invitation');
        }
    };

    const handleToggleDisable = async (member) => {
        const disabling = !member.disabled;
        const message = disabling
            ? `Disable ${member.email}?\n\nThey will be locked out immediately. Their assignments and history are kept and they can be re-enabled at any time.`
            : `Re-enable ${member.email}?\n\nTheir access is restored exactly as it was.`;
        if (!window.confirm(message)) return;

        try {
            const response = await apiFetch(
                `${process.env.REACT_APP_API_URL}/client/${disabling ? 'disable-user' : 'enable-user'}`,
                {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ targetEmail: member.email })
                }
            );
            const data = await response.json();
            if (response.ok) {
                if (data.inviteResent) {
                    alert('User re-enabled. Their invite email was re-sent (previous temporary password had expired).');
                }
                fetchData();
            } else {
                alert(data.error || 'Failed to update user');
            }
        } catch (err) {
            console.error('Disable/enable error:', err);
            alert('Failed to update user');
        }
    };

    const handleToggleAdmin = async (email, makeAdmin) => {
        if (email === userInfo.email) {
            alert('You cannot change your own admin status');
            return;
        }

        if (!window.confirm(`${makeAdmin ? 'Promote' : 'Demote'} ${email} ${makeAdmin ? 'to' : 'from'} administrator?`)) {
            return;
        }

        try {
            const response = await apiFetch(
                `${process.env.REACT_APP_API_URL}/client/update-role`,
                {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json'
                    },
                    body: JSON.stringify({
                        targetEmail: email,
                        newRole: makeAdmin ? 'admin' : 'user',
                        leadId: leadId,
                        timestamp: timestamp
                    })
                }
            );

            const data = await response.json();

            if (response.ok) {
                alert(`Successfully ${makeAdmin ? 'promoted' : 'demoted'} ${email}`);
                fetchData(); // Refresh the team list
            } else {
                setError(data.error || 'Failed to update role');
            }
        } catch (err) {
            console.error('Role update error:', err);
            setError('Failed to update role');
        }
    };

    const handleChangePassword = async () => {
        // Validation
        if (!passwordForm.currentPassword || !passwordForm.newPassword || !passwordForm.confirmPassword) {
            setError('All password fields are required');
            return;
        }

        if (passwordForm.newPassword !== passwordForm.confirmPassword) {
            setError('New passwords do not match');
            return;
        }

        if (passwordForm.newPassword.length < 8) {
            setError('Password must be at least 8 characters');
            return;
        }

        setChangingPassword(true);
        setError(null);

        try {
            const response = await apiFetch(
                `${process.env.REACT_APP_API_URL}/client/change-password`,
                {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json'
                    },
                    body: JSON.stringify({
                        email: userInfo.email,
                        currentPassword: passwordForm.currentPassword,
                        newPassword: passwordForm.newPassword
                    })
                }
            );

            const data = await response.json();

            if (response.ok) {
                alert('Password changed successfully!');
                setShowPasswordModal(false);
                setPasswordForm({
                    currentPassword: '',
                    newPassword: '',
                    confirmPassword: ''
                });
            } else {
                setError(data.error || 'Failed to change password');
            }
        } catch (err) {
            console.error('Password change error:', err);
            setError('Failed to change password');
        } finally {
            setChangingPassword(false);
        }
    };

    if (loading) {
        return (
            <div className="min-h-screen bg-gray-50 flex items-center justify-center">
            <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto"></div>
            <p className="mt-4 text-gray-600">Loading Admin...</p>
            </div>
            </div>
        );
    }

    const isAdmin = userInfo.role === 'admin';

    return (
        <div className="min-h-screen bg-gray-50">
        {/* Header */}
        <header className="bg-white shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
        <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold text-gray-900">
        {isAdmin ? 'Admin Portal' : 'My Account'}
        </h1>
        <button
        onClick={() => navigate('/client/project')}
        className="text-primary-600 hover:text-primary-800 flex items-center gap-2"
        >
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
        </svg>
        Back to Dashboard
        </button>
        </div>
        </div>
        </header>

        {/* Main Content */}
        <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {error && (
            <div className="mb-4 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded">
            {error}
            </div>
        )}

        {/* Tabs */}
        <div className="bg-white rounded-lg shadow">
        <div className="border-b border-gray-200">
        <nav className="flex -mb-px">
        <button
        onClick={() => setActiveTab('account')}
        className={`px-6 py-4 text-sm font-medium border-b-2 ${
            activeTab === 'account'
            ? 'border-primary-600 text-primary-600'
            : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
        }`}
        >
        My Account
        </button>
        {isAdmin && (
            <button
            onClick={() => setActiveTab('team')}
            className={`px-6 py-4 text-sm font-medium border-b-2 ${
                activeTab === 'team'
                ? 'border-primary-600 text-primary-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
            >
            Team Members
            </button>
        )}
        </nav>
        </div>

        <div className="p-6">
        {/* My Account Tab */}
        {activeTab === 'account' && (
            <div className="space-y-6">
            <div>
            <h2 className="text-lg font-medium text-gray-900 mb-4">Account Information</h2>

            <div className="bg-gray-50 rounded-lg p-6 space-y-4">
            <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Name</label>
            <p className="text-gray-900">{userInfo.name}</p>
            </div>

            <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
            <p className="text-gray-900">{userInfo.email}</p>
            </div>

            <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Company</label>
            <p className="text-gray-900">{userInfo.company}</p>
            </div>

            <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Role</label>
            <span className={`inline-flex px-3 py-1 text-sm font-medium rounded-full ${
                isAdmin
                ? 'bg-primary-100 text-primary-800'
                : 'bg-gray-100 text-gray-800'
            }`}>
            {isAdmin ? 'Administrator' : 'Team Member'}
            </span>
            </div>
            </div>

            <div className="mt-6 flex gap-3">
            <button
            onClick={() => setShowPasswordModal(true)}
            className="btn-secondary"
            >
            Change Password
            </button>
            </div>
            </div>

            {/* Connector credentials live here, in My Account, because they
                belong to the person rather than to the project (Bill,
                2026-08-07). */}
            <div>
            <h2 className="text-lg font-medium text-gray-900 mb-4">Connect Claude</h2>
            <div className="bg-gray-50 rounded-lg p-6">
            <p className="text-sm text-gray-600 mb-4">
            Ask Claude about this project from your phone or laptop &mdash; your tasks,
            dates, risks and comments &mdash; without opening the portal.
            </p>
            <button
            onClick={() => setShowConnectClaude(true)}
            className="btn-secondary"
            >
            Set up connector
            </button>
            </div>
            </div>
            </div>
        )}

        {/* Team Members Tab - Only for Admins */}
        {activeTab === 'team' && isAdmin && (
            <div className="space-y-6">
            <div className="flex justify-between items-center">
            <h2 className="text-lg font-medium text-gray-900">Team Members</h2>
            <button
            onClick={() => { setError(null); setShowInviteModal(true); }}
            className="btn-primary flex items-center gap-2"
            >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
            </svg>
            Invite Colleague
            </button>
            </div>

            <div className="space-y-3">
            {teamMembers.map((member, idx) => (
                <div key={idx} className="bg-gray-50 rounded-lg p-4 flex items-center justify-between">
                <div className="flex items-center gap-4">
                <div className="w-10 h-10 rounded-full bg-primary-100 flex items-center justify-center">
                <span className="text-primary-600 font-medium text-sm">
                {member.name.split(' ').map(n => n[0]).join('').toUpperCase()}
                </span>
                </div>
                <div>
                <p className="font-medium text-gray-900">{member.name}</p>
                <p className="text-sm text-gray-600">{member.email}</p>
                <div className="flex items-center gap-2 mt-1">
                {(() => {
                    const isSelf = String(member.email || '').trim().toLowerCase() === String(userInfo.email || '').trim().toLowerCase();
                    return (
                <label
                className={`flex items-center gap-2 ${isSelf ? 'cursor-not-allowed opacity-60' : 'cursor-pointer'}`}
                title={isSelf ? 'You cannot change your own role' : undefined}
                >
                <input
                type="checkbox"
                checked={member.role === 'admin'}
                disabled={isSelf}
                onChange={(e) => handleToggleAdmin(member.email, e.target.checked)}
                className="w-4 h-4 text-primary-600 rounded focus:ring-2 focus:ring-primary-500 disabled:cursor-not-allowed"
                />
                <span className="text-sm text-gray-700">
                Client Administrator{isSelf ? ' (you)' : ''}
                </span>
                </label>
                    );
                })()}
                <span className={`inline-flex px-2 py-0.5 text-xs font-medium rounded ${
                    member.status === 'disabled'
                    ? 'bg-red-100 text-red-800'
                    : member.status === 'active'
                    ? 'bg-green-100 text-green-800'
                    : 'bg-yellow-100 text-yellow-800'
                }`}>
                {member.status === 'disabled' ? 'Disabled' : member.status === 'active' ? 'Active' : 'Pending'}
                </span>
                <span className={`inline-flex items-center gap-1 text-xs ${member.mfaEnabled ? 'text-green-600' : 'text-gray-400'}`}>
                <FaShieldAlt size={11} />
                MFA {member.mfaEnabled ? 'on' : 'off'}
                </span>
                </div>
                </div>
                </div>

                {member.role !== 'admin' && (
                    <div className="flex gap-2">
                    {member.status === 'pending' && (
                        <button
                        onClick={() => handleResendInvite(member)}
                        className="text-sm text-primary-600 hover:text-primary-800"
                        >
                        Resend Invite
                        </button>
                    )}
                    <button
                    onClick={() => handleToggleDisable(member)}
                    className={`text-sm ${member.disabled ? 'text-green-600 hover:text-green-800' : 'text-red-600 hover:text-red-800'}`}
                    >
                    {member.disabled ? 'Enable' : 'Disable'}
                    </button>
                    </div>
                )}
                </div>
            ))}
            </div>
            </div>
        )}
        </div>
        </div>
        </main>

        {/* Invite Modal - Only for Admins */}
        {showInviteModal && isAdmin && (
            <div className="fixed inset-0 bg-gray-900 bg-opacity-50 flex items-center justify-center p-4 z-50">
            <div className="bg-white rounded-lg shadow-xl max-w-md w-full">
            <div className="p-6">
            <div className="flex justify-between items-center mb-4">
            <h3 className="text-lg font-medium text-gray-900">Invite Colleague</h3>
            <button
            onClick={() => {
                setShowInviteModal(false);
                setInviteForm({ name: '', email: '' });
            }}
            className="text-gray-400 hover:text-gray-600"
            >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
            </button>
            </div>

            <div className="space-y-4">
            <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
            Name *
            </label>
            <input
            type="text"
            value={inviteForm.name}
            onChange={(e) => setInviteForm({...inviteForm, name: e.target.value})}
            className="w-full px-3 py-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-primary-500"
            placeholder="John Smith"
            />
            </div>

            <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
            Email *
            </label>
            <input
            type="email"
            value={inviteForm.email}
            onChange={(e) => setInviteForm({...inviteForm, email: e.target.value})}
            className="w-full px-3 py-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-primary-500"
            placeholder="john@company.com"
            />
            </div>

            <div className="bg-blue-50 border border-blue-200 rounded p-3">
            <p className="text-sm text-blue-800">
            An invitation email will be sent with read-only access to the project.
            </p>
            </div>
            </div>

            {error && (
                <div className="mt-4 bg-red-50 border border-red-200 text-red-700 px-3 py-2 rounded text-sm">
                {error}
                </div>
            )}

            <div className="mt-6 flex gap-3">
            <button
            onClick={handleInviteUser}
            disabled={inviting}
            className="btn-primary flex-1"
            >
            {inviting ? 'Sending...' : 'Send Invitation'}
            </button>
            <button
            onClick={() => {
                setShowInviteModal(false);
                setInviteForm({ name: '', email: '' });
            }}
            className="btn-secondary"
            >
            Cancel
            </button>
            </div>
            </div>
            </div>
            </div>
        )}

        {/* Change Password Modal */}
        {showPasswordModal && (
            <div className="fixed inset-0 bg-gray-900 bg-opacity-50 flex items-center justify-center p-4 z-50">
            <div className="bg-white rounded-lg shadow-xl max-w-md w-full">
            <div className="p-6">
            <div className="flex justify-between items-center mb-4">
            <h3 className="text-lg font-medium text-gray-900">Change Password</h3>
            <button
            onClick={() => {
                setShowPasswordModal(false);
                setPasswordForm({
                    currentPassword: '',
                    newPassword: '',
                    confirmPassword: ''
                });
                setError(null);
            }}
            className="text-gray-400 hover:text-gray-600"
            >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
            </button>
            </div>

            <div className="space-y-4">
            <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
            Current Password *
            </label>
            <input
            type="password"
            value={passwordForm.currentPassword}
            onChange={(e) => setPasswordForm({...passwordForm, currentPassword: e.target.value})}
            className="w-full px-3 py-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-primary-500"
            placeholder="Enter current password"
            />
            </div>

            <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
            New Password *
            </label>
            <input
            type="password"
            value={passwordForm.newPassword}
            onChange={(e) => setPasswordForm({...passwordForm, newPassword: e.target.value})}
            className="w-full px-3 py-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-primary-500"
            placeholder="Enter new password"
            />
            <p className="text-xs text-gray-500 mt-1">Must be at least 8 characters</p>
            </div>

            <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
            Confirm New Password *
            </label>
            <input
            type="password"
            value={passwordForm.confirmPassword}
            onChange={(e) => setPasswordForm({...passwordForm, confirmPassword: e.target.value})}
            className="w-full px-3 py-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-primary-500"
            placeholder="Confirm new password"
            />
            </div>

            {error && (
                <div className="bg-red-50 border border-red-200 text-red-700 px-3 py-2 rounded text-sm">
                {error}
                </div>
            )}
            </div>

            <div className="mt-6 flex gap-3">
            <button
            onClick={handleChangePassword}
            disabled={changingPassword}
            className="btn-primary flex-1"
            >
            {changingPassword ? 'Changing...' : 'Change Password'}
            </button>
            <button
            onClick={() => {
                setShowPasswordModal(false);
                setPasswordForm({
                    currentPassword: '',
                    newPassword: '',
                    confirmPassword: ''
                });
                setError(null);
            }}
            className="btn-secondary"
            >
            Cancel
            </button>
            </div>
            </div>
            </div>
            </div>
        )}


        <ConnectClaudeModal
            isOpen={showConnectClaude}
            onClose={() => setShowConnectClaude(false)}
            apiUrl={process.env.REACT_APP_API_URL}
            mfaEnabled={mfaEnabled}
            mfaStatusFailed={mfaStatusFailed}
            onSetUpMfa={() => { setShowConnectClaude(false); setShowSecurityModal(true); }}
        />
        <SecurityModal
            isOpen={showSecurityModal}
            onClose={() => setShowSecurityModal(false)}
            apiUrl={process.env.REACT_APP_API_URL}
            onStatusChange={(enabled) => { setMfaEnabled(enabled); setMfaStatusFailed(false); }}
        />
        </div>
    );
}
